import React, { useState, useEffect, useContext } from 'react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { 
  Shield, Search, Filter, Download, User, Calendar, FileText, 
  Activity, Clock, Eye, Upload, Trash, Edit, MessageSquare,
  ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage, translations } from '@/contexts/LanguageContext.jsx';

const actionIcons = {
  'document_upload': Upload,
  'document_delete': Trash,
  'document_view': Eye,
  'chat_query': MessageSquare,
  'table_extraction': FileText,
  'document_comparison': FileText,
  'export_report': Download,
  'login': User,
  'default': Activity
};

const actionColors = {
  'document_upload': 'text-green-400 bg-green-400/10',
  'document_delete': 'text-red-400 bg-red-400/10',
  'document_view': 'text-blue-400 bg-blue-400/10',
  'chat_query': 'text-purple-400 bg-purple-400/10',
  'table_extraction': 'text-yellow-400 bg-yellow-400/10',
  'document_comparison': 'text-indigo-400 bg-indigo-400/10',
  'export_report': 'text-cyan-400 bg-cyan-400/10',
  'login': 'text-green-400 bg-green-400/10',
  'default': 'text-slate-400 bg-slate-400/10'
};

export const AuditLogPage = () => {
  const token = localStorage.getItem('token');
  const { language } = useLanguage();
  const t = translations[language];
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: '',
    resource_type: '',
    user_id: '',
    start_date: '',
    end_date: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadLogs();
    loadStats();
  }, []);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.action) params.append('action', filters.action);
      if (filters.resource_type) params.append('resource_type', filters.resource_type);
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await axios.get(`${API}/audit/logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data.logs || []);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(t.adminAccessRequired);
      } else {
        toast.error(t.failedLoadAuditLogs);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/audit/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const applyFilters = () => {
    loadLogs();
  };

  const clearFilters = () => {
    setFilters({
      action: '',
      resource_type: '',
      user_id: '',
      start_date: '',
      end_date: ''
    });
    setTimeout(loadLogs, 100);
  };

  const getIcon = (action) => {
    const Icon = actionIcons[action] || actionIcons.default;
    return Icon;
  };

  const getColorClass = (action) => {
    return actionColors[action] || actionColors.default;
  };

  return (
    <div className="p-8 bg-slate-950 min-h-full">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white tracking-tight flex items-center gap-3">
          <Shield className="text-indigo-400" />
          {t.auditTrail}
        </h1>
        <p className="text-base text-slate-400 mt-2">{t.trackAllActivities}</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                  <Activity className="text-indigo-400" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{logs.length}</p>
                  <p className="text-xs text-slate-400">{t.totalEvents}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <User className="text-green-400" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.top_users?.length || 0}</p>
                  <p className="text-xs text-slate-400">{t.activeUsers}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <FileText className="text-purple-400" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.actions?.length || 0}</p>
                  <p className="text-xs text-slate-400">{t.actionTypes}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                  <Calendar className="text-yellow-400" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.daily_activity?.length || 0}</p>
                  <p className="text-xs text-slate-400">{t.daysTracked}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-slate-900/50 border-slate-800 mb-6">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="text-slate-400" size={18} />
              {t.filters}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setShowFilters(!showFilters)}>
              {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </Button>
          </div>
        </CardHeader>
        {showFilters && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <Select value={filters.action} onValueChange={v => setFilters({...filters, action: v})}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue placeholder={t.actionType} />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all">{t.allActions}</SelectItem>
                  <SelectItem value="document_upload">{t.documentUpload}</SelectItem>
                  <SelectItem value="document_delete">{t.documentDelete}</SelectItem>
                  <SelectItem value="chat_query">{t.chatQuery}</SelectItem>
                  <SelectItem value="table_extraction">{t.tableExtraction}</SelectItem>
                  <SelectItem value="document_comparison">{t.documentComparison}</SelectItem>
                  <SelectItem value="export_report">{t.exportReport}</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filters.resource_type} onValueChange={v => setFilters({...filters, resource_type: v})}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue placeholder={t.resourceType} />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all">{t.allResources}</SelectItem>
                  <SelectItem value="document">{t.document}</SelectItem>
                  <SelectItem value="report">{t.report}</SelectItem>
                  <SelectItem value="user">{t.user}</SelectItem>
                </SelectContent>
              </Select>

              <Input
                type="date"
                placeholder={t.startDateLabel}
                value={filters.start_date}
                onChange={e => setFilters({...filters, start_date: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white"
              />

              <Input
                type="date"
                placeholder={t.endDateLabel}
                value={filters.end_date}
                onChange={e => setFilters({...filters, end_date: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white"
              />

              <div className="flex gap-2">
                <Button onClick={applyFilters} className="bg-indigo-600 hover:bg-indigo-700 flex-1">
                  <Search size={16} className="mr-2" />
                  {t.apply}
                </Button>
                <Button onClick={clearFilters} variant="outline" className="border-slate-600">
                  {t.clear}
                </Button>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Logs Table */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <CardTitle>{t.activityLog}</CardTitle>
          <CardDescription>{t.showingEvents?.replace('{count}', logs.length) || `Showing ${logs.length} events`}</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-400">{t.loading}</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              {t.noAuditLogs}
            </div>
          ) : (
            <div className="space-y-3">
              {logs.map((log, index) => {
                const Icon = getIcon(log.action);
                const colorClass = getColorClass(log.action);
                
                return (
                  <div key={log.id || index} className="flex items-start gap-4 p-4 bg-slate-800/50 rounded-lg">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClass}`}>
                      <Icon size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{log.action?.replace(/_/g, ' ')}</span>
                        {log.resource_type && (
                          <span className="text-xs px-2 py-0.5 bg-slate-700 rounded text-slate-400">
                            {log.resource_type}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-slate-400 mt-1">
                        {log.user_email || t.system} 
                        {log.resource_id && <span className="text-slate-500"> • {log.resource_id.substring(0, 20)}...</span>}
                      </div>
                      {log.details && Object.keys(log.details).length > 0 && (
                        <div className="text-xs text-slate-500 mt-2 font-mono">
                          {JSON.stringify(log.details)}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500 flex items-center gap-1">
                        <Clock size={12} />
                        {new Date(log.timestamp).toLocaleString()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogPage;
